import { useMemo } from 'react';
import { useGlobalQuery } from '@/state/query-client/hooks';
import {
  getDepartmentOptions,
  getDesignationOptions,
  getEmployeesByUserIds,
  searchEmployees,
} from '../services/lookup.service';
import { DepartmentLookupOption, EmployeeOption, LookupOption } from '../types/lookup.types';

const LOOKUP_STALE_TIME = 30 * 60 * 1000;

export const useDepartmentOptions = () =>
  useGlobalQuery<DepartmentLookupOption[]>({
    queryKey: ['hr-lookup-departments'],
    queryFn: getDepartmentOptions,
    staleTime: LOOKUP_STALE_TIME,
  });

export const useDesignationOptions = () =>
  useGlobalQuery<LookupOption[]>({
    queryKey: ['hr-lookup-designations'],
    queryFn: getDesignationOptions,
    staleTime: LOOKUP_STALE_TIME,
  });

export const useEmployeeSearch = (keyword: string, departmentId?: string) =>
  useGlobalQuery<EmployeeOption[]>({
    queryKey: ['hr-lookup-employees', keyword, departmentId ?? ''],
    queryFn: () => searchEmployees(keyword, departmentId),
    staleTime: 60 * 1000,
  });

/** Employees keyed by IAM user id, for showing names next to the ids review records store. */
export const useEmployeesByUserIds = (userIds: string[]) => {
  const ids = useMemo(() => [...new Set(userIds.filter(Boolean))].sort(), [userIds]);
  const { data, isLoading } = useGlobalQuery<EmployeeOption[]>({
    queryKey: ['hr-lookup-employees-by-user-ids', ids],
    queryFn: () => getEmployeesByUserIds(ids),
    enabled: ids.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  const byUserId = useMemo(
    () => new Map((data ?? []).map((employee) => [employee.UserId, employee])),
    [data]
  );
  return { byUserId, isLoading };
};
